import { CheckCircle2, FileEdit, Mail, Send, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { QuotationStatus } from '../types/quotation.types';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  icon: React.ComponentType<{ className?: string }>;
  /** Spin the icon (Posting in-flight). */
  spin?: boolean;
  /** Extra classes layered over the variant (info/destructive tint for the posting states). */
  className?: string;
}

// Total Record — every QuotationStatus MUST have an entry or the unguarded lookup renders `undefined`.
// 86d3mnuxe: Posting/PostingFailed are no longer entity statuses (outbox states now) — the posting
// overlay is applied via the optional postingStatus prop instead.
const statusConfig: Record<QuotationStatus, StatusConfig> = {
  [QuotationStatus.Draft]: { label: 'Draft', variant: 'outline', icon: FileEdit },
  [QuotationStatus.Submitted]: { label: 'Submitted', variant: 'success', icon: CheckCircle2 },
  [QuotationStatus.AutoDraft]: { label: 'Auto-draft', variant: 'secondary', icon: Mail },
  [QuotationStatus.Sent]: { label: 'Sent', variant: 'success', icon: Send },
};

// 86d3mnuxe — outbox posting overlays (info spinner / destructive triangle; same tokens as SO).
const POSTING_CONFIG: StatusConfig = {
  label: 'Posting…', variant: 'outline', icon: Loader2, spin: true,
  className: 'border-transparent bg-info/10 text-info',
};
const FAILED_CONFIG: StatusConfig = {
  label: 'Failed', variant: 'outline', icon: AlertTriangle,
  className: 'border-transparent bg-destructive/10 text-destructive',
};

interface QuotationStatusBadgeProps {
  status: QuotationStatus;
  subLabel?: string;
  /** 86d3mnuxe — outbox posting overlay ("Posting"/"Failed") for a quotation with an in-flight /
   *  dead-lettered posting.cl job; overrides the entity status. */
  postingStatus?: 'Posting' | 'Failed' | null;
  /** Failure reason (outbox LastError) — the badge tooltip on a failed post. */
  failedReason?: string | null;
}

export function QuotationStatusBadge({ status, subLabel, postingStatus, failedReason }: QuotationStatusBadgeProps) {
  const config =
    postingStatus === 'Posting' ? POSTING_CONFIG : postingStatus === 'Failed' ? FAILED_CONFIG : statusConfig[status];
  const Icon = config.icon;
  const tooltip =
    postingStatus === 'Failed'
      ? (failedReason ?? 'Posting failed — open the quotation to retry')
      : undefined;

  return (
    <Badge
      variant={config.variant}
      className={cn('gap-1 text-xs cursor-default', config.className)}
      title={tooltip}
    >
      <Icon className={cn('h-3 w-3', config.spin && 'animate-spin')} />
      <span>{subLabel ?? config.label}</span>
    </Badge>
  );
}
